import i18n from "@/i18n";
import { AiModel } from "@/pages/Private/redux/media/media.schema";
import {
	MAX_SCENE_DURATION_SECONDS,
	MediaErrorCode,
	SceneArtifactState,
	VideoWizard,
	WizardStepStatus,
} from "@/pages/Private/redux/media/videoWizard.schema";

/** The six step keys, in order — used for i18n lookups and stepper rendering. */
export const STEP_KEYS = [
	"storyboard",
	"style",
	"narration",
	"audio",
	"sceneVideos",
	"cutExport",
] as const;

/** The step whose model decides how long a scene is allowed to be. */
export const SCENE_VIDEO_STEP = 5;

/** The viewer's own number locale — "$0,02" in an English UI read as two mixed locales. */
const numberLocale = () => (i18n.language?.startsWith("de") ? "de-DE" : "en-US");

/**
 * Every figure in the wizard comes from the server, so this only ever formats — it never
 * computes. Formatted in the UI's own locale, decimals and all.
 */
export const formatUsd = (amount: number): string =>
	`$${(amount ?? 0).toLocaleString(numberLocale(), {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

/**
 * Seconds in the UI's locale — 6,4 s in German, 6.4 s in English — so the value never
 * mixes registers with the buttons beside it. A non-breaking space before the unit: a
 * narrow badge must never wrap "0" over "s".
 */
export const formatSeconds = (seconds: number): string =>
	`${(seconds ?? 0).toLocaleString(numberLocale(), { maximumFractionDigits: 1 })}\u00A0s`;

export const stepOf = (wizard: VideoWizard | undefined, step: number) =>
	wizard?.steps.find((entry) => entry.step === step);

export const isStepApproved = (wizard: VideoWizard | undefined, step: number) =>
	stepOf(wizard, step)?.status === WizardStepStatus.APPROVED;

export const isStepLocked = (wizard: VideoWizard | undefined, step: number) =>
	!!wizard && step > wizard.unlockedStep;

/** How full the calculation frame is. Purely a display figure — nothing is ever blocked. */
export const budgetPercent = (wizard: VideoWizard | undefined): number => {
	const max = wizard?.costs.budgetBand.maxUsd ?? 0;

	if (!wizard || max <= 0) {
		return 0;
	}

	return Math.min(100, Math.round((wizard.costs.forecastUsd / max) * 100));
};

export const isOverBudgetBand = (wizard: VideoWizard | undefined): boolean =>
	!!wizard && wizard.costs.forecastUsd > wizard.costs.budgetBand.maxUsd;

/**
 * Whether the server still owes this wizard a result: a job that has not reached a
 * terminal status, or a scene artifact reserved for one. Both are what put a card in its
 * generating state, so both are what it takes to get out of one.
 */
export const hasWorkInFlight = (wizard: VideoWizard | undefined): boolean =>
	!!wizard &&
	(wizard.activeJobs.length > 0 ||
		wizard.storyboard.scenes.some(
			(scene) =>
				scene.image?.state === SceneArtifactState.GENERATING ||
				scene.video?.state === SceneArtifactState.GENERATING
		));

/**
 * The longest scene step 4 could actually render, which is what step 1 has to bound its
 * duration input by — a scene past it is one the wizard could never finish, and step 1 is
 * the only place able to prevent one.
 *
 * The model deciding that is the one chosen for step 4, or the registry default until the
 * author picks one. A model that publishes no ceiling, or a list that has not loaded yet,
 * falls back to the same static bound the API enforces.
 */
export const maxSceneDurationOf = (
	wizard: VideoWizard | undefined,
	models: AiModel[] | undefined
): number => {
	const selectedId = stepOf(wizard, SCENE_VIDEO_STEP)?.aiModelId;
	const model =
		(models ?? []).find((entry) => entry.id === selectedId) ??
		(models ?? []).find((entry) => entry.isDefault);

	return model?.maxSceneDurationSeconds ?? MAX_SCENE_DURATION_SECONDS;
};

/** Step 1 approval needs the scenes to add up to the target exactly — the server's rule. */
export const isLengthExact = (wizard: VideoWizard | undefined): boolean =>
	!!wizard && wizard.storyboard.totalDurationSeconds === wizard.targetLengthSeconds;

/**
 * The job failures the web words itself, by code. The API's text is German in every UI
 * language and, for a refusal, the provider's own JSON used to reach the card (BCM360-372);
 * a code listed here is shown in the UI's language, anything else as the API sent it.
 */
const FAILURE_TEXT_KEYS: Partial<Record<string, string>> = {
	[MediaErrorCode.PROVIDER_MODERATED]: "errors.providerModerated",
	[MediaErrorCode.PROVIDER_IMAGE_REFUSED]: "errors.providerImageRefused",
};

/** The `videoWizard.*` key for a failure code the web words itself, if it is one. */
export const failureTextKey = (code?: string | null): string | undefined =>
	code ? FAILURE_TEXT_KEYS[code] : undefined;
